import { QuestionnaireCategoryItemsItemOneOf as Question } from '@/api/generated-catch-backend/model';
import { AnimatePresence, motion } from 'motion/react';
import { RefObject, useEffect, useRef } from 'react';
import { NavigationDirection } from '../../context/types';
import WhyWeAsk from '../survey-item/components/why-we-ask';
import { SurveyItem } from '../survey-item/survey-item';
import { OnNext } from '../types';

const getSlideVariants = (direction: NavigationDirection) => ({
  initial: { x: direction === 'forward' ? 50 : -50, opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: direction === 'forward' ? -50 : 50, opacity: 0 },
});

const slideTransition = {
  duration: 0.2,
  ease: 'easeOut' as const,
};

export function QuestionnaireCard({
  isWhyWeAskExpanded,
  setIsWhyWeAskExpanded,
  currentQuestion,
  handleNext,
  questionTitleRef,
  isLoading,
  navigationDirection = 'forward',
  onSkip,
}: {
  isWhyWeAskExpanded: boolean;
  setIsWhyWeAskExpanded: (isExpanded: boolean) => void;
  currentQuestion: Question;
  handleNext: OnNext;
  questionTitleRef?: RefObject<HTMLDivElement>;
  isLoading?: boolean;
  navigationDirection?: NavigationDirection;
  onSkip?: () => void;
}) {
  const slideVariants = getSlideVariants(navigationDirection);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Reset scroll position when the question changes
  useEffect(() => {
    scrollContainerRef.current?.scrollTo?.(0, 0);
  }, [currentQuestion?.slug]);

  return (
    <div className="relative flex min-h-0 flex-1 w-full flex-col overflow-hidden">
      <AnimatePresence mode="wait">
        {!isWhyWeAskExpanded && (
          <motion.div
            key="questionnaire-content"
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="questionnaire-card-content min-h-0 flex-1 overflow-hidden pb-0"
          >
            <div ref={scrollContainerRef} className="mx-auto flex min-h-0 w-full max-w-[700px] flex-1 items-start justify-center overflow-y-auto overscroll-contain">
              <AnimatePresence mode="wait">
                {currentQuestion ? (
                  <motion.div
                    key={currentQuestion.slug}
                    variants={slideVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={slideTransition}
                    className="w-full"
                  >
                    <SurveyItem
                      surveyItem={currentQuestion}
                      isLoading={isLoading}
                      onWhyWeAskExpand={() => setIsWhyWeAskExpanded(true)}
                      hideWhyWeAsk={isWhyWeAskExpanded}
                      onNext={handleNext}
                      questionTitleRef={questionTitleRef}
                      onSkip={onSkip}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
        {isWhyWeAskExpanded && currentQuestion && (
          <WhyWeAsk
            key="why-we-ask-expanded"
            explanation={currentQuestion.whyWeAsk}
            isExpanded={isWhyWeAskExpanded}
            isFullScreen={true}
            onClose={() => setIsWhyWeAskExpanded(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
